/**
 * Decimal handling for compliance figures.
 *
 * The UI never calls `Number(x)` on a backend figure. Every numeric value
 * arrives as a JSON string with explicit basis (per `API_CONTRACT.md` §3) and
 * is wrapped in a Decimal instance here. Display formatting goes through
 * `formatDecimal`; arithmetic uses Decimal's own methods.
 */
import Decimal from "decimal.js";

export { Decimal };

/**
 * Strict parser: accepts the canonical backend string form only.
 * No locale formatting, no thousands separators, no scientific notation.
 *
 * Valid:   "49572", "-419.18", "0.5421", "1234567.89"
 * Invalid: "49,572", "$554,529", "1.2345e-2", "  49572  ", "", "NaN", "1.0a"
 */
export function parseDecimalString(raw: string): Decimal {
  if (typeof raw !== "string") {
    throw new TypeError(`parseDecimalString expected string, got ${typeof raw}`);
  }
  if (raw.length === 0) {
    throw new RangeError("parseDecimalString: empty string");
  }
  // Strict regex: optional sign, digits, optional decimal point + digits.
  if (!/^-?\d+(\.\d+)?$/.test(raw)) {
    throw new RangeError(`parseDecimalString: not a canonical decimal string: "${raw}"`);
  }
  const d = new Decimal(raw);
  if (!d.isFinite()) {
    throw new RangeError(`parseDecimalString: non-finite value: "${raw}"`);
  }
  return d;
}

/**
 * True when `raw` is missing/blank or a canonical zero. Any non-canonical or
 * unparseable string ("", "NaN", a localized "1,234") counts as "not zero" and
 * NEVER throws — so a render-time "is this figure empty?" check can't take out
 * the page on a bad backend value (the guarded counterpart to a bare
 * `new Decimal(raw).isZero()`).
 */
export function isZeroOrBlank(raw: string | null | undefined): boolean {
  if (raw == null || raw === "") return true;
  try {
    return parseDecimalString(raw).isZero();
  } catch {
    return false;
  }
}

export type FormatDecimalOptions = {
  /** Fixed decimal places to display. */
  decimalPlaces?: number;
  /** Insert thousands separators (default true). */
  thousandsSeparator?: boolean;
  /** Render as a percent of the value (multiplies by 100 and appends `%`). */
  asPercent?: boolean;
};

/**
 * Display formatter. Never used for round-tripping back to the backend.
 * The caller passes a Decimal in; a string comes out.
 */
export function formatDecimal(d: Decimal, opts: FormatDecimalOptions = {}): string {
  const { decimalPlaces, thousandsSeparator = true, asPercent = false } = opts;
  let value = d;
  if (asPercent) value = value.times(100);

  const rounded =
    decimalPlaces === undefined
      ? value
      : value.toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP);
  const [int, frac] = rounded.toFixed(decimalPlaces ?? rounded.decimalPlaces()).split(".");

  // The `int` segment is always present after toFixed; the assertion is for the type checker.
  const intGrouped =
    thousandsSeparator && int !== undefined ? int.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : int;
  const out = frac !== undefined ? `${intGrouped}.${frac}` : intGrouped;
  return asPercent ? `${out}%` : (out ?? "");
}
